/* global Office */
import { useState, useCallback, useEffect, useRef } from "react";
import {
  activatePasswordManager,
  deactivatePasswordManager,
  getPasswordManagerStatus,
} from "../api/passwordManagerApi";
import {
  handleOfficeDialogMessage,
  setupDevModeBridge,
} from "../services/sidebarBridge";

/**
 * Custom hook for password manager state management
 * Handles activation, deactivation, status checks, and bridge message routing.
 *
 * @param {string} oid - User's Azure AD Graph ID
 * @param {Function} onSuccess - Callback for success notifications
 * @param {Function} onError - Callback for error notifications
 * @param {Function} getCryptoState - Returns current CryptoContext state snapshot
 * @param {object} cryptoActions - { unlock, lock } from CryptoContext
 * @returns {Object} Password manager state and actions
 */
export const usePasswordManager = (oid, onSuccess, onError, getCryptoState, cryptoActions, userEmail) => {
  const [isActive, setIsActive] = useState(false);
  const [databaseId, setDatabaseId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Store callbacks in refs to avoid dependency loop
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  // Store crypto refs so bridge always has latest state
  const getCryptoStateRef = useRef(getCryptoState);
  const cryptoActionsRef = useRef(cryptoActions);

  // Track if initial load has been done to prevent multiple calls
  const hasInitializedRef = useRef(false);

  // Cleanup function for dev mode bridge
  const devBridgeCleanupRef = useRef(null);

  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    getCryptoStateRef.current = getCryptoState;
  }, [getCryptoState]);

  useEffect(() => {
    cryptoActionsRef.current = cryptoActions;
  }, [cryptoActions]);

  /**
   * Loads password manager status from backend
   * Note: Does NOT set loading state - only user actions should show loading
   */
  const loadStatus = useCallback(async () => {
    if (!oid) return;

    const response = await getPasswordManagerStatus(oid);

    if (response.success && response.data) {
      setIsActive(response.data.passwortManagerAktiv ?? false);
      setDatabaseId(response.data.aktivPasswortDatenbankId ?? null);
    }
  }, [oid]);

  /**
   * Activates password manager with crypto material
   */
  const activate = useCallback(
    async (cryptoMaterial) => {
      if (!oid) {
        if (onErrorRef.current)
          onErrorRef.current("Keine Benutzer-ID gefunden");
        return;
      }

      if (!cryptoMaterial) {
        if (onErrorRef.current) {
          onErrorRef.current(
            "Krypto-Material fehlt. Bitte führen Sie die Ersteinrichtung durch."
          );
        }
        return;
      }

      try {
        setLoading(true);

        const dto = {
          oid: oid,
          publicKey: cryptoMaterial.publicKey,
          encryptedPrivateKey: cryptoMaterial.encryptedPrivateKey,
          encryptedMasterKey: cryptoMaterial.encryptedMasterKey,
          masterKeyEnvelope: cryptoMaterial.masterKeyEnvelope,
        };

        const response = await activatePasswordManager(dto);

        if (response.success) {
          await loadStatus();

          if (onSuccessRef.current) {
            onSuccessRef.current("Passwortdatenbank erfolgreich aktiviert");
          }
        } else {
          if (onErrorRef.current) {
            onErrorRef.current(response.message);
          }
        }
      } catch (error) {
        console.error("Error activating password manager:", error);
        if (onErrorRef.current) {
          onErrorRef.current("Fehler beim Aktivieren der Passwortdatenbank");
        }
      } finally {
        setLoading(false);
      }
    },
    [oid, loadStatus]
  );

  /**
   * Deactivates password manager
   */
  const deactivate = useCallback(async () => {
    if (!oid) {
      if (onErrorRef.current) onErrorRef.current("Keine Benutzer-ID gefunden");
      return;
    }

    try {
      setLoading(true);
      const response = await deactivatePasswordManager(oid);

      if (response.success) {
        setIsActive(false);

        if (onSuccessRef.current) {
          onSuccessRef.current("Passwortdatenbank erfolgreich deaktiviert");
        }
      } else {
        if (onErrorRef.current) {
          onErrorRef.current(response.message);
        }
      }
    } catch (error) {
      console.error("Error deactivating password manager:", error);
      if (onErrorRef.current) {
        onErrorRef.current("Fehler beim Deaktivieren der Passwortdatenbank");
      }
    } finally {
      setLoading(false);
    }
  }, [oid]);

  /**
   * Opens password manager in separate Office Dialog window.
   * Sets up the bridge for sidebar <-> dialog communication.
   */
  const openManager = useCallback(async () => {
    try {
      setLoading(true);

      // Step 1: Clear stale session data so the dialog starts fresh
      sessionStorage.removeItem("dialog_oid");
      sessionStorage.removeItem("dialog_accessToken");
      sessionStorage.removeItem("dialog_sessionId");
      sessionStorage.removeItem("dialog_expiresAt");
      sessionStorage.removeItem("dialog_exchange_in_progress");

      // Step 2: Create dialog session
      const { createDialogSession } = await import("../api/dialogSessionApi");
      const sessionResult = await createDialogSession(oid);

      if (!sessionResult.success) {
        if (onErrorRef.current) onErrorRef.current(sessionResult.message);
        return;
      }

      const sessionData = sessionResult.data;
      console.log("Dialog session created:", sessionData.sessionId);

      // Step 2: Open dialog with session ID
      const emailParam = userEmail ? `&userEmail=${encodeURIComponent(userEmail)}` : "";
      const dialogUrl = `${window.location.origin}${sessionData.dialogUrl}${emailParam}`;

      // Check if Office.js is available
      if (
        typeof Office === "undefined" ||
        !Office.context ||
        !Office.context.ui
      ) {
        console.warn(
          "Office.js not available - opening in new browser window (dev mode)"
        );

        // Dev mode: open in new window + set up postMessage bridge
        const devDialog = window.open(dialogUrl, "PasswordManager", "width=1200,height=800");

        if (devDialog) {
          // Clean up previous bridge if any
          if (devBridgeCleanupRef.current) {
            devBridgeCleanupRef.current();
          }

          // Set up postMessage bridge for dev mode
          devBridgeCleanupRef.current = setupDevModeBridge(
            () => getCryptoStateRef.current ? getCryptoStateRef.current() : {},
            cryptoActionsRef.current || {},
            devDialog,
          );

          // Poll for window close to sync sidebar state
          const pollTimer = setInterval(() => {
            if (devDialog.closed) {
              clearInterval(pollTimer);
              console.log("[Dev] Dialog window closed, reloading status");
              loadStatus();
              // Clean up bridge
              if (devBridgeCleanupRef.current) {
                devBridgeCleanupRef.current();
                devBridgeCleanupRef.current = null;
              }
            }
          }, 500);
        }
        return;
      }

      // Production: Use Office Dialog API
      Office.context.ui.displayDialogAsync(
        dialogUrl,
        { height: 85, width: 75, displayInIframe: false },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            console.error("Dialog failed to open:", asyncResult.error);
            if (onErrorRef.current) {
              onErrorRef.current("Fehler beim Öffnen des Passwort-Managers");
            }
          } else {
            const dialog = asyncResult.value;

            // Handle dialog messages (bridge + legacy pm-deactivated)
            dialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              async (arg) => {
                console.log("Message from dialog:", arg.message);

                // Try bridge message first
                const bridgeResponse = await handleOfficeDialogMessage(
                  arg.message,
                  () => getCryptoStateRef.current ? getCryptoStateRef.current() : {},
                  cryptoActionsRef.current || {},
                );

                if (bridgeResponse) {
                  // Bridge message – send response back via messageChild
                  try {
                    dialog.messageChild(bridgeResponse);
                  } catch (err) {
                    console.error("[Bridge] Failed to send messageChild:", err);
                  }
                  return;
                }

                // Legacy message handling (pm-deactivated)
                try {
                  const data = JSON.parse(arg.message);
                  if (data.type === "pm-deactivated") {
                    dialog.close();
                    setIsActive(false);
                    setDatabaseId(null);
                    if (onSuccessRef.current) {
                      onSuccessRef.current(
                        "Passwort-Manager wurde deaktiviert \u2013 keine Datenbank mehr vorhanden."
                      );
                    }
                  }
                } catch (_) {
                  // Not JSON - ignore
                }
              }
            );

            // Handle dialog close — reload status to sync sidebar state
            dialog.addEventHandler(
              Office.EventType.DialogEventReceived,
              (arg) => {
                if (arg.error === 12006) {
                  console.log("Dialog closed by user");
                }
                loadStatus();
              }
            );
          }
        }
      );
    } catch (error) {
      console.error("Error opening password manager:", error);
      if (onErrorRef.current) {
        onErrorRef.current("Fehler beim Öffnen des Passwort-Managers");
      }
    } finally {
      setLoading(false);
    }
  }, [oid, loadStatus]);

  // Load status on mount
  useEffect(() => {
    if (hasInitializedRef.current || !oid) {
      return;
    }

    hasInitializedRef.current = true;
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid]);

  // Cleanup dev bridge on unmount
  useEffect(() => {
    return () => {
      if (devBridgeCleanupRef.current) {
        devBridgeCleanupRef.current();
      }
    };
  }, []);

  return {
    isActive,
    databaseId,
    loading,
    loadStatus,
    activate,
    deactivate,
    openManager,
  };
};
